import { ResolvedTheme } from '../context/ThemeContext';

type HighchartsThemeOptions = Record<string, unknown>;

// ──────────────────────────────────────────────────────────────────────
// Theme builder — consolidates the shape of each Highcharts theme so
// adding a new theme is a simple parameter change.
// ──────────────────────────────────────────────────────────────────────

interface ThemeTokens {
    text: string;
    textHover: string;
    subtleText: string;
    gridLine: string;
    axisLine: string;
    tooltipBg: string;
    tooltipBorder: string;
    legendActive: string;
    legendInactive: string;
    legendHidden: string;
    pieLabelOutline: string;
    navButtonFill: string;
    navButtonStroke: string;
}

function makeTheme(t: ThemeTokens): HighchartsThemeOptions {
    return {
        chart: { backgroundColor: 'transparent' },
        title: { style: { color: t.text } },
        subtitle: { style: { color: t.subtleText } },
        xAxis: {
            labels: { style: { color: t.subtleText } },
            gridLineColor: t.gridLine,
            lineColor: t.axisLine,
            tickColor: t.axisLine,
        },
        yAxis: {
            labels: { style: { color: t.subtleText } },
            gridLineColor: t.gridLine,
            title: { style: { color: t.subtleText } },
        },
        legend: {
            itemStyle: { color: t.text },
            itemHoverStyle: { color: t.textHover },
            itemHiddenStyle: { color: t.legendHidden },
            navigation: {
                activeColor: t.legendActive,
                inactiveColor: t.legendInactive,
                style: { color: t.text, fontWeight: 'bold' },
            },
        },
        tooltip: {
            backgroundColor: t.tooltipBg,
            style: { color: t.text },
            borderColor: t.tooltipBorder,
        },
        plotOptions: {
            pie: {
                dataLabels: {
                    style: { color: t.text, textOutline: t.pieLabelOutline },
                    connectorColor: t.subtleText,
                },
            },
        },
        navigation: {
            buttonOptions: {
                theme: { fill: t.navButtonFill, stroke: t.navButtonStroke },
                symbolStroke: t.subtleText,
            },
        },
    };
}

// ──────────────────────────────────────────────────────────────────────
// Theme tokens — one object per theme
// ──────────────────────────────────────────────────────────────────────

const themeMap: Record<ResolvedTheme, HighchartsThemeOptions> = {
    light: makeTheme({
        text: '#333',
        textHover: '#000',
        subtleText: '#666',
        gridLine: '#e6e6e6',
        axisLine: '#ccd6eb',
        tooltipBg: '#fff',
        tooltipBorder: '#999',
        legendActive: '#003399',
        legendInactive: '#cccccc',
        legendHidden: '#ccc',
        pieLabelOutline: '1px contrast',
        navButtonFill: '#fff',
        navButtonStroke: '#ccd6eb',
    }),
    dark: makeTheme({
        text: '#e0e0e0',
        textHover: '#fff',
        subtleText: '#adb5bd',
        gridLine: '#444460',
        axisLine: '#444460',
        tooltipBg: '#2a2a3c',
        tooltipBorder: '#555',
        legendActive: '#6ea8fe',
        legendInactive: '#555',
        legendHidden: '#555',
        pieLabelOutline: 'none',
        navButtonFill: '#2a2a3c',
        navButtonStroke: '#444460',
    }),
    midnight: makeTheme({
        text: '#e0e0e0',
        textHover: '#fff',
        subtleText: '#8391a2',
        gridLine: '#1a4a6e',
        axisLine: '#1a4a6e',
        tooltipBg: '#0a2d4e',
        tooltipBorder: '#1a4a6e',
        legendActive: '#6ea8fe',
        legendInactive: '#3f72a4',
        legendHidden: '#3f72a4',
        pieLabelOutline: 'none',
        navButtonFill: '#02203e',
        navButtonStroke: '#1a4a6e',
    }),
};

export function getHighchartsThemeOptions(theme: ResolvedTheme): HighchartsThemeOptions {
    return themeMap[theme];
}
